import {
    AssetTypeEnum,
    AssetTypeLabels,
    SiteReccePriorityEnum,
    SiteReccePriorityLabels,
    SiteRecceTypeEnum,
    SiteRecceTypeLabels,
} from '@api/enum';
import DocFileIcon from '@components/DocFileIcon/DocFileIcon';
import FormikDatePicker from '@components/FormFields/FormikDatePicker';
import { Button, Card, HelpText, Label } from '@hyperflake/react-ui-library';
import { FormHelperText } from '@library/forms';
import { getFileExtension } from '@utils/file-utils';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikInput, FormikSelect, FormikSwitch } from 'modules/shared/features/formik';
import FormikTextarea from 'modules/shared/features/formik/components/FormikTextarea/FormikTextarea';
import { useSiteRecceAddForm } from 'modules/site-recce/hooks/useSiteRecceAddForm';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const AddSiteRecceForm = () => {
    const { clientInstallationId, projectInstallationId } = useParams();
    const formHook = useSiteRecceAddForm(clientInstallationId!, projectInstallationId!);
    const navigate = useNavigate();

    const priorityOptions = useMemo(
        () =>
            Object.values(SiteReccePriorityEnum).map((priority) => ({
                label: SiteReccePriorityLabels[priority], // Use human-readable label
                value: priority,
            })),
        []
    );

    const typeOptions = useMemo(() => {
        return [
            { label: 'Select Type', value: '' },
            ...Object.values(SiteRecceTypeEnum).map((val) => ({
                label: SiteRecceTypeLabels[val], // Display human-readable label
                value: val,
            })),
        ];
    }, []);

    const assetTypeOptions = useMemo(() => {
        return [
            { label: 'Select Asset', value: '' },
            ...Object.values(AssetTypeEnum).map((val) => ({
                label: AssetTypeLabels[val], // Display human-readable label
                value: val,
            })),
        ];
    }, []);

    // const typeOptions = useMemo(() => Object.values(SiteRecceTypeEnum).map((t) => ({ label: t, value: t })), []);

    // const assetTypeOptions = useMemo(() => Object.values(AssetTypeEnum).map((a) => ({ label: a, value: a })), []);

    const handleSubmit = async (values: any) => {
        try {
            const data = await formHook.submit(values);
            toast.success('Recce Added Successfully');
            navigate(
                `/client-installations/${clientInstallationId}/project-installations/${projectInstallationId}/recce`
            );
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };

    return (
        <Formik
            initialValues={formHook.initialValues}
            enableReinitialize
            validationSchema={formHook.schema}
            onSubmit={handleSubmit}
        >
            {({ values, setFieldValue, isSubmitting }) => (
                <Form>
                    <Card className="mt-4">
                        <Card.Header>Site Recce Details</Card.Header>
                        <Card.Body>
                            {/* Type */}
                            <div>
                                <Label>Type</Label>
                                <FormikSelect name="type" options={typeOptions} />
                                <HelpText variant="error">
                                    <ErrorMessage name="type" />
                                </HelpText>
                            </div>

                            {/* Asset Type */}
                            <div className="mt-4">
                                <Label>Asset Type</Label>
                                <FormikSelect name="assetType" options={assetTypeOptions} />
                                <HelpText variant="error">
                                    <ErrorMessage name="assetType" />
                                </HelpText>
                            </div>
                            {/*Quantity */}
                            <div className="mt-4">
                                <Label>Quantity</Label>
                                <FormikInput name="quantity" type="number" />
                                <HelpText variant="error">
                                    <ErrorMessage name="quantity" />
                                </HelpText>
                            </div>
                            {/* Priority */}
                            <div className="mt-4">
                                <Label>Priority</Label>
                                <FormikSelect name="priority" options={priorityOptions} />
                                <HelpText variant="error">
                                    <ErrorMessage name="priority" />
                                </HelpText>
                            </div>

                            {/* Date */}
                            <div className="mt-4">
                                <Label>Preferred Date</Label>
                                <FormikDatePicker name="preferredDate" isClearable />
                                <FormHelperText variant={'error'}>
                                    <ErrorMessage name="preferredDate" />
                                </FormHelperText>
                            </div>
                            {/* <div className="mt-4">
                                <Label>Date (YYYYMMDD)</Label>
                                <FormikInput name="date" />
                                <HelpText variant="error">
                                    <ErrorMessage name="date" />
                                </HelpText>
                            </div> */}

                            {/* PO Received */}

                            <div className="flex items-center gap-2">
                                <Label htmlFor="poRecieved" className="font-medium text-grayscale-900 mb-0">
                                    PO Received
                                </Label>

                                <FormikSwitch color="success" name="poRecieved" />
                            </div>
                            {/* <div className="mt-4">
                                <Label>PO Received</Label>
                                <FormikSelect
                                    name="poRecieved"
                                    options={[
                                        { label: 'Yes', value: true },
                                        { label: 'No', value: false },
                                    ]}
                                />
                                <HelpText variant="error">
                                    <ErrorMessage name="poRecieved" />
                                </HelpText>
                            </div> */}
                            {/* Remarks */}
                            <div className="mt-4">
                                <Label>Remarks</Label>
                                <FormikTextarea name="remarks" rows={4} />
                                <HelpText variant="error">
                                    <ErrorMessage name="remarks" />
                                </HelpText>
                            </div>
                        </Card.Body>
                    </Card>
                    <Card className="mt-4">
                        <Card.Header>Documents</Card.Header>
                        <Card.Body>
                            {/* File Upload */}
                            <div className="mt-4">
                                <Label>Upload Files</Label>
                                <input
                                    type="file"
                                    multiple
                                    className="ml-4"
                                    onChange={(event) => {
                                        const selectedFiles = event.currentTarget.files;
                                        if (selectedFiles) {
                                            setFieldValue('files', Array.from(selectedFiles));
                                        }
                                    }}
                                />
                                <ul className="mt-2 list-disc pl-5">
                                    {values.files.map((file: File, idx: number) => (
                                        <li key={idx} className="flex  items-center gap-2">
                                            <DocFileIcon className="w-7" extension={getFileExtension(file.name)} />
                                            {file.name}
                                            <Button
                                                size="xs"
                                                type="button"
                                                className="ml-2"
                                                onClick={() => {
                                                    const updated = [...values.files];
                                                    updated.splice(idx, 1);
                                                    setFieldValue('files', updated);
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Card.Body>
                    </Card>

                    <Button type="submit" className="mt-6  w-full sm:w-auto" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Add Recce'}
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

export default AddSiteRecceForm;
